/**
 * Listing of people and organizations that support Khan Academy.
 */

import {Trans} from "@lingui/react/macro";
import {css, StyleSheet, type CSSProperties} from "aphrodite";
import * as React from "react";

import Link from "@khanacademy/wonder-blocks-link";
import {color, font, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Caption} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslate} from "@khan/i18n";
import {mediaQueries} from "@khan/shared-styles";

import {moduleLayout} from "#modules-package/styles.ts";
import DonateLink from "#page-package/donate-link.tsx";

import {
    BLANK,
    getSections,
    type Section,
    type Supporter,
} from "./supporters-data.ts";

const SupportersPage = (): React.ReactElement<React.ComponentProps<"div">> => {
    return (
        <div>
            <SubHeading />

            {getSections().map((section, i) => {
                if (section.title) {
                    return <SupportersSection key={i} {...section} />;
                }

                if (section.divider) {
                    return (
                        <DividerSection
                            key={i}
                            kind={section.divider}
                            url={section.url}
                        />
                    );
                }
            })}

            <FinalMessage />
        </div>
    );
};

const SubHeading = (): JSX.Element => (
    <div className={css(styles.calloutContainer)}>
        <h2 className={css(styles.calloutHeader)}>
            <Trans>
                We would like to thank our generous supporters who have helped
                us grow and kept our core operations running. Join our community
                of thousands of supporters from around the world!{" "}
                <DonateLink
                    inline={true}
                    urlParams={{
                        c_src2: "Webapp.supporters",
                    }}
                >
                    Donate to Khan Academy
                </DonateLink>{" "}
                or increase your impact by{" "}
                <Link
                    inline={true}
                    href="https://donate.khanacademy.org/campaign/the-learners-fund/c511350"
                    target="_blank"
                >
                    supporting the Learners Fund
                </Link>
                .
            </Trans>
        </h2>
    </div>
);

function SupportersSection(section: Section) {
    const columns = section.columns?.map(
        (column: Array<Supporter>, colIndex) => {
            const rows = column.map((row: Supporter, rowIndex) => (
                <SupporterItem
                    key={rowIndex}
                    row={row}
                    rowIndex={rowIndex}
                    colIndex={colIndex}
                />
            ));

            return (
                <ul className={css(styles.supportersList)} key={colIndex}>
                    {rows}
                </ul>
            );
        },
    );

    return (
        <div className={css(styles.sectionContainer)}>
            <h3 className={css(styles.sectionTitle)}>{section.title}</h3>
            {section.subtitle && <Caption>{section.subtitle}</Caption>}

            <div className={css(styles.sectionColumns)}>{columns}</div>
        </div>
    );
}

type SupporterItemProps = {
    row: Supporter;
    rowIndex: number;
    colIndex: number;
};
function SupporterItem({
    row,
    rowIndex,
    colIndex,
}: SupporterItemProps): JSX.Element {
    // A supporter row can either display a heading (donation amount),
    // a supporter name (string). A BLANK character can be passed
    // in as a string to display an empty row to align across
    // columns.
    const supporterStyles: Array<CSSProperties> = [];
    let content: string | JSX.Element = "";

    if (row === BLANK) {
        // Blank row content & styles
        content = <br />;
        supporterStyles.push(styles.supportersBlankRow);
    } else if (typeof row === "string") {
        // Name row content & styles
        content = row;
        supporterStyles.push(styles.supportersName);

        if (colIndex === 0 && rowIndex === 0) {
            supporterStyles.push(styles.supportersFirstColRow);
        }
    } else if (row.heading) {
        // Heading row content & styles
        content = row.heading;
        supporterStyles.push(styles.supportersHeading);

        if (rowIndex > 0) {
            supporterStyles.push(styles.supportersHeadingGap);
        }
        if (colIndex === 0 && rowIndex === 0) {
            supporterStyles.push(styles.supportersFirstColRow);
        }
    }

    return <li className={css(...supporterStyles)}>{content}</li>;
}

type DividerSectionProps = {
    kind: string;
    url?: string;
};
const DividerSection = ({kind, url}: DividerSectionProps): JSX.Element => {
    return (
        <>
            {kind === "default" && (
                <div className={css(styles.dividerContainer)}>
                    <hr className={css(styles.dividerHorizontal)} />
                </div>
            )}

            {kind === "image" && url && (
                <div
                    className={css(styles.featuredPhoto)}
                    style={{
                        backgroundImage: url,
                    }}
                />
            )}
        </>
    );
};

const FinalMessage = (): JSX.Element => (
    <p className={css(styles.disclaimerMessage)}>
        <Trans>
            We strive to be as accurate as possible when recognizing our donors.
            If a mistake is noticed, please contact us at{" "}
            <Link href="mailto:donate@khanacademy.org" inline={true}>
                {doNotTranslate`donate@khanacademy.org`}
            </Link>
            .
        </Trans>
    </p>
);

const styles = StyleSheet.create({
    calloutContainer: {
        backgroundColor: color.offWhite,
    },

    calloutHeader: {
        ...moduleLayout.defaultSpacing,
        maxWidth: 670,
        margin: "auto",
        fontWeight: "normal",

        [mediaQueries.mdOrSmaller]: {
            fontSize: font.size.medium,
            lineHeight: font.lineHeight.medium,
            fontWeight: "normal",
        },
    },

    sectionContainer: {
        ...moduleLayout.defaultAlignment,
        ...moduleLayout.defaultSpacing,
        paddingTop: 0,
        paddingBottom: 0,
        fontWeight: "normal",
        color: "#191b21",

        [mediaQueries.mdOrSmaller]: {
            paddingTop: 0,
            paddingBottom: 0,
        },
    },

    sectionTitle: {
        fontSize: font.size.xxxLarge,
        lineHeight: font.lineHeight.xxxLarge,
        ...moduleLayout.defaultAlignment,
        marginTop: spacing.xxLarge_48,
        color: color.offBlack,

        [mediaQueries.mdOrSmaller]: {
            fontSize: font.size.xLarge,
            lineHeight: font.lineHeight.xLarge,
            marginTop: spacing.xLarge_32,
        },
    },

    sectionColumns: {
        display: "flex",
        marginTop: spacing.xxLarge_48,
        marginBottom: spacing.xxLarge_48,

        [mediaQueries.mdOrSmaller]: {
            flexDirection: "column",
            marginTop: spacing.xLarge_32,
            marginBottom: spacing.xLarge_32,
        },
    },

    supportersList: {
        fontSize: font.size.large,
        lineHeight: font.lineHeight.large,
        flex: 1,

        [mediaQueries.lgOrLarger]: {
            ":not(:last-child)": {
                marginRight: spacing.small_12,
            },
        },

        [mediaQueries.mdOrSmaller]: {
            fontSize: font.size.medium,
            lineHeight: font.lineHeight.medium,
        },
    },

    supportersHeading: {
        fontWeight: "bold",

        [mediaQueries.mdOrSmaller]: {
            marginTop: 37,
        },
    },

    supportersHeadingGap: {
        marginTop: 37,
    },

    supportersName: {
        lineHeight: 1.25,

        ":not(:first-child)": {
            marginTop: spacing.medium_16,
        },

        [mediaQueries.mdOrSmaller]: {
            marginTop: spacing.medium_16,
        },
    },

    supportersFirstColRow: {
        marginTop: 0,

        [mediaQueries.mdOrSmaller]: {
            marginTop: 0,
        },
    },

    supportersBlankRow: {
        display: "inline-block",

        [mediaQueries.mdOrSmaller]: {
            display: "none",
        },
    },

    dividerContainer: {
        ...moduleLayout.defaultAlignment,
    },

    dividerHorizontal: {
        borderColor: color.offBlack16,
        borderStyle: "solid",
        borderWidth: "0 0 1px",
        boxShadow: "none",
        height: 0,
        margin: 0,
    },

    featuredPhoto: {
        height: 300,
        backgroundSize: "cover",
        backgroundPosition: "50%",
    },

    disclaimerMessage: {
        fontSize: font.size.large,
        lineHeight: font.lineHeight.large,
        ...moduleLayout.defaultSpacing,
        maxWidth: 620,
        margin: "auto",

        [mediaQueries.mdOrSmaller]: {
            fontSize: font.size.medium,
            lineHeight: font.lineHeight.medium,
        },
    },
});

export default SupportersPage;
